import type {
  AlertEnvironment, AlertGroup, AlertKind, AlertPayload, AlertSource, AlertStatus, CheckerMode, ComplianceSurface,
  RiskTimelineEventType, SignalId, SignalState,
} from '@core/api/complianceTypes';
import { kindGroup } from '@core/api/complianceRules';
import { formatDateTime } from '@shared/formatters/date';
import { SIGNAL_BY_ID } from './signals';

/**
 * The plain-words dictionary of the console (display plan section 3). The rule: the plain words lead; the code
 * (S-id, A-number, rule id, uuid, hash) stays beside or under them, or as the hover title. An unknown code renders
 * as the code, never hidden. No em dashes (the owner's product copy rule).
 *
 * Compliance review (2026-09-27) rulings applied here: a checker rule reads as what the checker FLAGGED
 * ("Telling the trader what to do"), never as a thing the AI did to the trader, so a held-back reply is never
 * described as advice given; S2 and S3 say what the record shows; "signals", not "concerns"; the next step for an
 * AI-content matter points at the monthly audit review, where the text can be read today.
 */

// ─── Statuses, sources, environments, creators ─────────────────────────────────────────────────

export const STATUS_PLAIN: Record<AlertStatus, string> = { new: 'Untouched', acknowledged: 'In hand', resolved: 'Done' };

export const SOURCE_PLAIN: Record<AlertSource, string> = {
  'aws-event': 'AWS security event',
  'cloudwatch-alarm': 'AWS alarm',
  'ecs-task': 'Export job',
  derived: 'The hourly risk check',
  manual: 'An admin',
};

export function environmentPlain(environment: AlertEnvironment): string {
  return environment === 'account' ? 'AWS account (all environments)' : environment;
}

/** `admin:jdoe` reads as `jdoe` everywhere, Created by included. */
export function stripAdmin(actor: string): string {
  return actor.replace(/^admin:/, '');
}

export function createdByPlain(createdBy: string): string {
  if (createdBy === 'poller') return 'AWS feed';
  if (createdBy === 'evaluator') return 'the hourly risk check';
  if (createdBy.startsWith('admin:')) return `${stripAdmin(createdBy)} (admin)`;
  return createdBy;
}

// ─── The checker on a surface ───────────────────────────────────────────────────────────────

/**
 * The checker mode of a surface, honestly: a surface with no rows had no activity (never "unchecked"), and only
 * Consult chat has no checker at all. A null mode on a checked surface with rows means the rows did not record one.
 */
export function checkerPlain(mode: CheckerMode | null, rows: number, surface: ComplianceSurface, window = '30 days'): string {
  if (mode === 'enforce') return 'Enforcing';
  if (mode === 'shadow') return 'Shadow: records, does not hold back';
  if (rows === 0) return `No activity in ${window}`;
  return surface === 'backtest-chat' ? 'No checker on this surface' : 'Checker mode not recorded';
}

// ─── Signals ────────────────────────────────────────────────────────────────────────────────

export function signalPlain(id: SignalId): string {
  return SIGNAL_BY_ID.get(id)?.plain ?? id;
}

const SIGNAL_STATE_PLAIN: Record<SignalState, string> = { lit: 'Over the line', clear: 'Under the line', 'not-recorded': 'Not tracked yet' };

export function signalStatePlain(state: SignalState): string {
  return SIGNAL_STATE_PLAIN[state];
}

// ─── Alert kinds ────────────────────────────────────────────────────────────────────────────

interface KindWords {
  sentence: string;
  code: string | null;
  next: string;
  /** Tells two kinds with the same sentence apart in the Kind select. */
  qualifier?: string;
}

const TRAIL_NEXT = 'If it was not you, escalate today and record the AWS identity.';
const COPY_NEXT = 'Open the task\'s log stream or run the copied query; if it repeats tomorrow, raise it with the engineer.';
const CHECKER_NEXT = 'Read the held-back requests in the monthly audit review, one review per request; closing is off here until the console can show that read.';

const FIXED_KINDS: Readonly<Record<string, KindWords>> = {
  'trail-tampered':          { sentence: 'The audit trail was stopped or changed', code: 'A1', next: TRAIL_NEXT },
  'audit-bucket-configured': { sentence: 'An audit bucket\'s protection settings were changed', code: 'A2', next: TRAIL_NEXT },
  'audit-object-touched':    { sentence: 'Someone tried to change or delete an audit file', code: 'A3', next: TRAIL_NEXT },
  'breakglass-assumed':      { sentence: 'The break-glass role was used', code: 'A4', next: TRAIL_NEXT },
  'export-task-failed':      { sentence: 'The nightly audit copy failed', code: 'A5', next: COPY_NEXT },
  'export-completed-absent': { sentence: 'No audit copy finished in 30 hours', code: 'A6', next: COPY_NEXT },
  'marker:audit-export.gap':                 { sentence: 'Audit copy problem: a gap in the chain', code: 'A7', next: COPY_NEXT },
  'marker:audit-export.backfill-required':   { sentence: 'Audit copy problem: a backfill is needed', code: 'A7', next: COPY_NEXT },
  'marker:audit-export.seal-refused':        { sentence: 'Audit copy problem: a seal was refused', code: 'A7', next: COPY_NEXT },
  'marker:audit-export.schema-not-current':  { sentence: 'Audit copy problem: the schema is out of date', code: 'A7', next: COPY_NEXT },
  'marker:audit-export.rows-disappeared':    { sentence: 'Audit copy problem: rows disappeared', code: 'A7', next: COPY_NEXT },
  'marker:audit-export.failed':              { sentence: 'Audit copy problem: the run failed', code: 'A7', next: COPY_NEXT },
  'marker:audit-export.ledger-write-failed': { sentence: 'Audit copy problem: the ledger could not be written', code: 'A7', next: COPY_NEXT },
  'marker:plan-my-day.prod-launch-blocked':    { sentence: 'A production host was configured with an AI feature on and was stopped', code: 'A8', next: 'Escalate today; confirm the flag is off in prod.', qualifier: 'Plan My Day' },
  'marker:ai-backtesting.prod-launch-blocked': { sentence: 'A production host was configured with an AI feature on and was stopped', code: 'A8', next: 'Escalate today; confirm the flag is off in prod.', qualifier: 'AI Backtester' },
  'marker:plan-my-day.assist-blocked':       { sentence: 'Many AI Assist answers held back within one hour', code: 'A9', next: CHECKER_NEXT },
  'marker:plan-my-day.recap-withheld':       { sentence: 'Several recaps held back within a day', code: 'A10', next: CHECKER_NEXT },
  'marker:plan-my-day.day-summary-withheld': { sentence: 'Several day summaries held back within a day', code: 'A10', next: CHECKER_NEXT },
  'marker:plan-my-day.assist-role-leak':      { sentence: 'The model wrote lines as the trader (cut out before showing)', code: 'A11', next: 'Check in the monthly audit review that nothing was finalised from the cut text.', qualifier: 'AI Assist' },
  'marker:plan-my-day.day-summary-role-leak': { sentence: 'The model wrote lines as the trader (cut out before showing)', code: 'A11', next: 'Check in the monthly audit review that nothing was finalised from the cut text.', qualifier: 'day summary' },
  'marker:problem-response': { sentence: 'AI Assist server errors within one hour', code: 'A12', next: 'Hand the correlation ids to the engineer.' },
  'marker:me.export':        { sentence: 'A burst of data exports', code: 'A13', next: 'Check it is not automation; a data request is the trader\'s right, so it is context, never a finding against anyone.' },
  'audit-escalation':        { sentence: 'Escalation from the monthly AI audit', code: 'A14', next: 'The AI supervisor reads the listed rows in the monthly audit review and records the outcome in a note.' },
  manual:                    { sentence: 'Raised by an admin', code: null, next: 'Read the note; close it when done.' },
  'user-reviewed':           { sentence: 'An admin marked this trader reviewed', code: null, next: 'Nothing: it is a record that someone looked.' },
  'aws-unclassified':        { sentence: 'An AWS message the inbox could not classify (kept)', code: null, next: 'Open the raw message; if it matters, raise a manual alert.' },
};

function signalOfKind(kind: AlertKind): SignalId | null {
  return kind.startsWith('signal:') ? kind.slice('signal:'.length) as SignalId : null;
}

/** The plain sentence of an alert kind. A3 says ", and AWS refused it" when the payload says so. */
export function kindPlain(kind: AlertKind, payload?: AlertPayload | null): string {
  const fixed = FIXED_KINDS[kind];
  if (kind === 'audit-object-touched' && payload?.denied === true) return `${fixed!.sentence}, and AWS refused it`;
  // AWS did not refuse the call: it went through, so the words must not understate it as an attempt (final review).
  if (kind === 'audit-object-touched' && payload?.denied === false) return 'Someone changed or deleted an audit file, and AWS did not refuse it';
  if (fixed) return fixed.sentence;
  const signal = signalOfKind(kind);
  if (signal) return `${signalPlain(signal)}, ${signal === 'S20' ? 'on new records' : 'for one trader'}`;
  if (kind.startsWith('marker:')) return `Alarm on marker ${kind.slice('marker:'.length)}`;
  return kind;
}

/** The inventory's A-number (plan 1.1), shown in mono under the sentence; null for manual and review records. */
export function kindCode(kind: AlertKind): string | null {
  const fixed = FIXED_KINDS[kind];
  if (fixed) return fixed.code;
  if (kind.startsWith('signal:')) return 'A15';
  if (kind.startsWith('marker:audit-export.')) return 'A7';
  if (kind.startsWith('marker:') && kind.endsWith('.prod-launch-blocked')) return 'A8';
  return null;
}

/** The next step for an alert kind: the catalog's reviewer action for a signal, the plan's sentence otherwise. */
export function kindNext(kind: AlertKind): string {
  const fixed = FIXED_KINDS[kind];
  if (fixed) return fixed.next;
  const signal = signalOfKind(kind);
  if (signal) return SIGNAL_BY_ID.get(signal)?.action ?? 'Open the trader\'s page.';
  return 'Open the alarm and its log query; if it matters, raise a manual alert.';
}

export const GROUP_LABEL: Record<AlertGroup, string> = {
  trail: 'Audit trail',
  copy: 'Audit copy',
  boundaries: 'Boundaries',
  checker: 'Answer checker',
  service: 'Service',
  audit: 'Monthly audit',
  traders: 'Traders',
  manual: 'Manual and reviews',
  unclassified: 'Unclassified',
};

/** The phase-2 signals, the ones a derived alert can carry. */
const DERIVED_SIGNALS: readonly SignalId[] = ['S1', 'S2', 'S3', 'S8', 'S9', 'S10', 'S11', 'S15', 'S17', 'S20'];

/** Every kind the inbox can filter on, in plain words, with its code and its group (the grouped Kind select). */
export const KIND_OPTIONS: readonly { value: AlertKind; label: string; code: string | null; group: AlertGroup }[] = [
  ...Object.entries(FIXED_KINDS).map(([value, w]) => ({
    value, label: w.qualifier ? `${w.sentence} (${w.qualifier})` : w.sentence, code: w.code, group: kindGroup(value),
  })),
  ...DERIVED_SIGNALS.map(s => ({ value: `signal:${s}`, label: kindPlain(`signal:${s}`), code: `A15 ${s}`, group: 'traders' as const })),
];

// ─── Checker rules ──────────────────────────────────────────────────────────────────────────

/** What the checker flagged, as a phrase that fits "the checker flagged the reply for ...". */
const RULE_PHRASE: Readonly<Record<string, string>> = {
  'advice-prescription': 'telling the trader what to do',
  'market-prediction': 'saying where the market will go',
  'contradicted-day-total': 'getting the day\'s totals wrong',
  'system-prompt-echo': 'repeating its own instructions',
  'format-envelope-breach': 'breaking the reply format',
  'control-override-claim': 'claiming the rules no longer apply',
  'advisor-persona': 'speaking as an adviser or giving a personal opinion',
  'broker-solicitation': 'pushing an account, a broker or more trading',
  'review-banned-word': 'using a word the review must never use',
  'behavioural-labelling': 'labelling the trader\'s character',
  'generic-maxim': 'offering a trading maxim',
  'ungrounded-fact': 'stating a fact it was not given',
  'unlabelled-reference-price': 'quoting a price without saying so',
  'invented-name': 'greeting the trader by an invented name',
  'greeting-opener': 'opening with a greeting',
  'role-label-leak': 'writing a line as the trader (cut out)',
};

/** A checker rule in words, capitalised ("Telling the trader what to do"); an unknown rule reads as its id. */
export function rulePlain(ruleId: string): string {
  const phrase = RULE_PHRASE[ruleId];
  return phrase ? phrase[0]!.toUpperCase() + phrase.slice(1) : ruleId;
}

/** Block and Flag read as what they do to the answer. */
export function ruleOutcome(severity: 0 | 1): string {
  return severity === 1 ? 'Held back' : 'Noted only';
}

/** One flag as a sentence that never says the AI did it: "The checker flagged the reply for ...; it was held back." */
export function ruleFlagSentence(ruleId: string, outcome: 'held-back' | 'shown' | null): string {
  const phrase = RULE_PHRASE[ruleId] ?? ruleId;
  const tail = outcome === 'held-back' ? '; it was held back.' : outcome === 'shown' ? '; it was shown.' : '.';
  return `The checker flagged the reply for ${phrase}${tail}`;
}

// ─── Views, timeline events, labels, payload keys ───────────────────────────────────────────

const VIEW_PLAIN: Readonly<Record<string, string>> = {
  assist_turns: 'AI Assist turn',
  outputs: 'Generated output',
  chat_turns: 'Consult chat turn',
  review_flags: 'Checker hit',
  prompt_usage: 'Model call',
  credit_ledger: 'Credit ledger entry',
  disclaimer_acceptances: 'Disclaimer acceptance',
  email_sends: 'Recap email send',
  users: 'The account itself',
  compliance_alerts: 'An alert',
};

const TABLE_TO_VIEW: Readonly<Record<string, string>> = {
  v2_day_assist_turns: 'assist_turns', v2_ai_outputs: 'outputs', v2_ai_chat_turns: 'chat_turns',
};

/** A view or table name in words (`assist_turns`, `v2_ai_audit_assist_turns`, `v2_day_assist_turns`); unknown ones stay raw. */
export function viewPlain(view: string): string {
  const key = TABLE_TO_VIEW[view] ?? view.replace(/^v2_ai_audit_/, '');
  return VIEW_PLAIN[key] ?? view;
}

const TIMELINE_PLAIN: Record<RiskTimelineEventType, string> = {
  'first-attempt-block': 'First answer held back, a rewrite was tried',
  'final-block': 'Trader got no answer',
  flag: 'Checker flag',
  'withheld-recap': 'Recap held back, the facts line was shown',
  'flagged-recap-emailed': 'A flagged recap went out by email',
  spend: 'Model spend',
  'uncharged-call': 'Call not charged',
  'admin-grant': 'Credits granted by an admin',
  acceptance: 'Accepted the disclaimer',
  stop: 'Trader pressed Stop',
  reset: 'Trader cleared the conversation',
  'dark-surface-row': 'AI activity where it should be off',
  'provenance-gap': 'A record the firm cannot fully explain',
  alert: 'Alert raised',
};

export function timelinePlain(type: RiskTimelineEventType | string): string {
  return TIMELINE_PLAIN[type as RiskTimelineEventType] ?? type;
}

const LABEL_PLAIN: Readonly<Record<string, string>> = {
  blocked: 'answer held back',
  rewritten: 'sent for rewrite',
  truncated: 'cut short',
  client_disconnect: 'trader disconnected',
  transport_error: 'connection error',
  empty_reply: 'empty reply',
  duplicate_request: 'double click absorbed',
  'no-current-acceptance': 'used AI without accepting the current disclaimer',
  'domain-not-in-allow-list': 'account is outside the firm and test domains',
  AdminGrant: 'credits granted by an admin',
};

export function labelPlain(label: string): string {
  return LABEL_PLAIN[label] ?? label;
}

const PAYLOAD_KEY_PLAIN: Readonly<Record<string, string>> = {
  count: 'Count in the window',
  threshold: 'Alarm threshold',
  windowMinutes: 'Window (minutes)',
  windowSeconds: 'Window (seconds)',
  windowDays: 'Window (days)',
  alarmName: 'Alarm',
  newState: 'Alarm state',
  principalArn: 'Who (AWS identity)',
  principalType: 'Kind of AWS identity',
  eventName: 'AWS call',
  bucket: 'Bucket',
  denied: 'Refused by AWS',
  errorCode: 'Error code',
  reason: 'Reason',
  exitCode: 'Exit code',
  evidenceIds: 'Rows behind it',
  idsTruncated: 'More rows than listed',
  reportSha256: 'Report hash',
  period: 'Audit period',
  signal: 'Signal',
  firstAttemptBlocks: 'Held back at first',
  finalBlocks: 'No answer shown',
  hits: 'Checker hits',
  rows: 'Rows',
  aiRows: 'AI rows',
  spendDays: 'High-spend days',
  absorbedCallDays: 'Days with many uncharged calls',
  adminGrants: 'Admin credit grants',
  withheld: 'Recaps held back',
  emailedFlagged: 'Flagged recaps emailed',
  signals: 'Signals over the line',
  gaps: 'Records the firm cannot fully explain',
  raisedBy: 'Raised by',
  reviewedBy: 'Reviewed by',
  litSignals: 'Signals over the line',
  openAlerts: 'Open alerts',
  evidenceItems: 'Evidence items',
  rowIds: 'Row ids',
};

/** A payload key in words, or null for a key the dictionary does not know (the drawer then shows it raw, in mono). */
export function payloadKeyPlain(key: string): string | null {
  return PAYLOAD_KEY_PLAIN[key] ?? null;
}

// ─── Fingerprints and rates ─────────────────────────────────────────────────────────────────

/** A derived alert's fingerprint as its meaning; null when it is not the evaluator's shape. */
export function fingerprintPlain(fingerprint: string): string | null {
  if (fingerprint.endsWith(':since-start')) return 'First time for this trader and signal';
  const m = /:since-(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(?:\.\d+)?Z$/.exec(fingerprint);
  if (!m) return null;
  const when = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`;
  return `Raised again after it was closed on ${formatDateTime(when)}`;
}

/** A rate honestly: "2 of 6 (33%)" under 20 answers, "33%" above, "none checked" with none. */
export function ratePlain(numerator: number, denominator: number): string {
  if (denominator <= 0) return 'none checked';
  const pct = `${Math.round((numerator / denominator) * 100)}%`;
  return denominator < 20 ? `${numerator} of ${denominator} (${pct})` : pct;
}

/** A rank in words: 1st, 2nd, 3rd, 4th, 11th, 12th, 13th, 21st. */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th'}`;
}

// ─── Months and the audit period ────────────────────────────────────────────────────────────

/** "September 2026" for `2026-09`. */
export function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** The UTC month `offset` months from `now`, as `YYYY-MM` (0 = this month, -1 = last month). */
export function monthOf(now: Date, offset: number): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Where "requests waiting for a review decision" starts counting (owner answer, 2026-09-27): the 1st of the previous
 * UTC month, so a request stays counted until the month it belongs to has been signed. "Aug 1, 2026".
 */
export function auditPeriodSince(now: Date): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Why the per-request review is shown but disabled (the parent plan's phase 3; "mark them as disabled in the FE",
 * 2026-09-27): it reads a trader's text and writes an access log, and this build does neither.
 */
export const REVIEW_NOT_BUILT =
  'The per-request review (this trader\'s requests and replies, supervisor only, every read logged) is not built: it reads customer text and writes an access log, and this build does neither yet. The monthly audit review is where the text is read today.';
